// Builds the static world from the render mesh: one chunked BufferGeometry set per material group.

import * as THREE from "three";
import { loadBakedLight } from "./bakedLight";
import type { BakedLight } from "./bakedLight";
import { createSurfaceMaterial } from "./materials/materials";
import type { MaterialDef } from "./materials/types";
import { mergePositions, splitGroup } from "./chunks";
import type { ChunkData } from "./chunks";
import { Slicer } from "./yield";
import { buildStaticDecals } from "./staticDecals";
import type { StaticDecals } from "./staticDecals";
import { createJumpPadVisual } from "./zones";
import type { PadVisual } from "./zones";
import { GlassPanes } from "./glass";
import type { MapFileJson } from "./mapTypes";
import type { RenderMeshData } from "../wasm";

const FALLBACK_MATERIAL: MaterialDef = {
  color: "#c9c7c2",
  texture: "plain",
  uvScale: 2,
  roughness: 0.9,
  metalness: 0,
  grime: 0.1,
};

const TEXTURE_SLOTS = ["map", "normalMap", "roughnessMap", "metalnessMap", "aoMap", "emissiveMap", "alphaMap"] as const;

export interface BuiltMap {
  /** Static geometry plus decor (jump pads). Add to the world scene. */
  root: THREE.Group;
  /** Union of the geometry bounds and the map's playable bounds. */
  bounds: THREE.Box3;
  triangles: number;
  chunks: number;
  /** Sets the anisotropy of every surface texture (re-uploads textures whose value changes). */
  setAnisotropy(level: number): void;
  /** The map's baked light (also at `root.userData.bakedLight`); null when the map has none or `bakedLight: false`. */
  baked: BakedLight | null;
  /** The breakable glass panes; null when the map has none. */
  glass: GlassPanes | null;
  dispose(): void;
}

/** Milliseconds of building between two yields to the event loop. */
const SLICE_MS = 8;

export interface BuildOptions {
  /** 0..1 over the material groups. */
  onProgress?: (fraction: number) => void;
  /** Checked between slices; the build stops and frees what it made. */
  cancelled?: () => boolean;
  /** Use the map's baked light (default true); false builds plain hemisphere + sun surfaces. */
  bakedLight?: boolean;
}

/**
 * Builds the map in slices that yield to the event loop (a single texture is the longest one).
 * Resolves to null when `opts.cancelled` turned true.
 */
export async function buildMap(map: MapFileJson, mesh: RenderMeshData, opts: BuildOptions = {}): Promise<BuiltMap | null> {
  const root = new THREE.Group();
  root.name = `map:${map.id}`;
  const bounds = new THREE.Box3(
    new THREE.Vector3(map.bounds.min[0], map.bounds.min[1], map.bounds.min[2]),
    new THREE.Vector3(map.bounds.max[0], map.bounds.max[1], map.bounds.max[2]),
  );
  const materials = new Map<string, THREE.MeshStandardMaterial>();
  const geometries: THREE.BufferGeometry[] = [];
  const pads: PadVisual[] = [];
  let decals: StaticDecals | null = null;
  let glass: GlassPanes | null = null;
  let triangles = 0;
  let chunkCount = 0;
  const geoBox = new THREE.Box3();
  /** Chunks that cast shadows through the merged meshes, by the side their material renders. */
  const shadowParts = new Map<THREE.Side, ChunkData[]>();
  const shadowMaterials: THREE.Material[] = [];

  const baked = opts.bakedLight === false ? null : await loadBakedLight(map);
  if (baked !== null && opts.cancelled?.()) {
    baked.dispose();
    return null;
  }
  root.userData.bakedLight = baked;

  const materialFor = (name: string): THREE.MeshStandardMaterial => {
    let m = materials.get(name);
    if (m === undefined) {
      m = createSurfaceMaterial(map.materials[name] ?? FALLBACK_MATERIAL, baked);
      m.name = name;
      materials.set(name, m);
    }
    return m;
  };

  const slicer = new Slicer(SLICE_MS);
  const disposeAll = (): void => {
    root.removeFromParent();
    for (const g of geometries) g.dispose();
    for (const m of materials.values()) m.dispose();
    baked?.dispose();
    for (const p of pads) p.dispose();
    decals?.dispose();
    glass?.dispose();
    for (const m of shadowMaterials) m.dispose();
  };

  for (let gi = 0; gi < mesh.groups.length; gi++) {
    const group = mesh.groups[gi];
    await slicer.turn();
    if (opts.cancelled?.()) {
      disposeAll();
      return null;
    }
    opts.onProgress?.(gi / mesh.groups.length);
    const material = materialFor(group.material);
    await slicer.turn();
    const transparent = material.transparent;
    const chunks = splitGroup(group.positions, group.normals, group.uvs, group.indices);
    for (const c of chunks) {
      await slicer.turn();
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(c.positions, 3));
      geo.setAttribute("normal", new THREE.BufferAttribute(c.normals, 3));
      geo.setAttribute("uv", new THREE.BufferAttribute(c.uvs, 2));
      geo.setIndex(new THREE.BufferAttribute(c.indices, 1));
      geo.boundingBox = new THREE.Box3(
        new THREE.Vector3(c.min[0], c.min[1], c.min[2]),
        new THREE.Vector3(c.max[0], c.max[1], c.max[2]),
      );
      geo.boundingSphere = geo.boundingBox.getBoundingSphere(new THREE.Sphere());
      geometries.push(geo);
      const m = new THREE.Mesh(geo, material);
      m.name = `${group.material}#${chunkCount}`;
      m.matrixAutoUpdate = false;
      // Plain opaque casters are drawn into the shadow map as part of one merged mesh (below); cut-out and
      // displaced surfaces need a depth material of their own and keep casting themselves.
      const merged = !transparent && material.alphaTest === 0 && material.alphaMap === null && material.displacementMap === null;
      m.castShadow = !transparent && !merged;
      m.receiveShadow = true;
      if (merged) {
        const parts = shadowParts.get(material.side) ?? [];
        parts.push(c);
        shadowParts.set(material.side, parts);
      }
      root.add(m);
      geoBox.union(geo.boundingBox);
      triangles += c.indices.length / 3;
      chunkCount++;
    }
  }
  if (mesh.breakable.length > 0) {
    glass = new GlassPanes(mesh.breakable, materialFor);
    root.add(glass.root);
    for (const g of mesh.breakable) triangles += g.indices.length / 3;
  }
  if (!geoBox.isEmpty()) bounds.union(geoBox);

  for (const [side, parts] of shadowParts) {
    const merged = mergePositions(parts);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(merged.positions, 3));
    geo.setIndex(new THREE.BufferAttribute(merged.indices, 1));
    geo.boundingSphere = new THREE.Sphere(bounds.getCenter(new THREE.Vector3()), bounds.getSize(new THREE.Vector3()).length());
    geometries.push(geo);
    const mat = new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false, side });
    shadowMaterials.push(mat);
    const caster = new THREE.Mesh(geo, mat);
    caster.name = `shadow:static:${side}`;
    caster.matrixAutoUpdate = false;
    caster.frustumCulled = false;
    caster.castShadow = true;
    // The colour pass has nothing to draw for it: an empty range there, the full mesh in the shadow pass.
    caster.onBeforeRender = () => geo.setDrawRange(0, 0);
    caster.onBeforeShadow = () => geo.setDrawRange(0, Infinity);
    root.add(caster);
  }

  for (const pad of map.jumpPads) {
    const v = createJumpPadVisual(pad);
    pads.push(v);
    root.add(v.group);
  }
  decals = buildStaticDecals(map.decals, baked);
  if (decals !== null) root.add(decals.mesh);
  root.updateMatrixWorld(true);

  let aniso = -1;
  return {
    root,
    bounds,
    triangles,
    chunks: chunkCount,
    baked,
    glass,
    setAnisotropy(level: number): void {
      if (level === aniso) return;
      aniso = level;
      for (const mat of materials.values()) {
        for (const slot of TEXTURE_SLOTS) {
          const tex = mat[slot];
          if (tex !== null && tex !== undefined && tex.anisotropy !== level) {
            tex.anisotropy = level;
            tex.needsUpdate = true;
          }
        }
      }
    },
    dispose: disposeAll,
  };
}
